/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Lightbulb, Plus, Bug, Rocket, Palette, ThumbsUp, MessageSquare, Trash2 } from 'lucide-react';
import { EmptyState } from '@/components/UIExtras';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { timeAgo } from '@/lib/utils-cat';
import { useAuth } from '@/lib/auth';

const CATEGORY_META = {
  feature: { icon: Rocket, color: 'var(--wl-primary)' },
  bug: { icon: Bug, color: '#B91C1C' },
  ux: { icon: Palette, color: '#7C3AED' },
  other: { icon: Lightbulb, color: '#475569' },
};
// Precomputed once — the filter was dead code (no 'paziente' key here).
const FEEDBACK_CATEGORIES = Object.entries(CATEGORY_META);

const STATUS_STYLES = {
  open: { bg: '#DBEAFE', color: '#1E3A8A' },
  in_progress: { bg: '#FEF3C7', color: '#78350F' },
  planned: { bg: '#EDE9FE', color: '#5B21B6' },
  done: { bg: '#DCFCE7', color: '#166534' },
  wont_do: { bg: '#F3F4F6', color: '#6B7280' },
};

const CreateDialog = ({ onCreated }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: 'feature', title: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post('/feedback', form);
      onCreated && onCreated(res.data);
      toast.success(t('save'));
      setOpen(false);
      setForm({ category: 'feature', title: '', description: '' });
    } catch { toast.error(t('errorOccurred')); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="feedback-create-open-button"><Plus className="h-4 w-4" />{t('newFeedback')}</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader><DialogTitle>{t('newFeedback')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-muted-foreground">{t('feedbackCategory')}</Label>
            <div className="mt-2 grid grid-cols-4 gap-1.5">
              {FEEDBACK_CATEGORIES.map(([k, meta]) => {
                const Icon = meta.icon; const active = form.category === k;
                return (
                  <button key={k} type="button" onClick={() => setForm(f => ({ ...f, category: k }))}
                    className={`p-2 rounded-xl border text-[11px] flex flex-col items-center gap-1 ${active ? 'ring-2 ring-primary/50 border-primary/50 bg-primary/5' : 'border-border hover:bg-secondary/50'}`}
                    data-testid={`feedback-cat-${k}`}>
                    <Icon className="h-4 w-4" style={{ color: meta.color }} />
                    {t(`fb_${k}`)}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('title')}</Label>
            <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} data-testid="feedback-title-input" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t('description')}</Label>
            <Textarea rows={4} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} data-testid="feedback-description-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button onClick={submit} disabled={submitting || !form.title.trim() || !form.description.trim()} data-testid="feedback-submit-button">
            {submitting ? t('loading') : t('publish')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AdminStatusControl = ({ item, onUpdated }) => {
  const { t } = useTranslation();
  const statuses = ['open', 'in_progress', 'planned', 'done', 'wont_do'];
  const [saving, setSaving] = useState(false);
  const change = async (s) => {
    setSaving(true);
    try {
      await api.put(`/feedback/${item.id}/status`, { status: s });
      onUpdated && onUpdated({ ...item, status: s });
      toast.success(t('save'));
    } catch { toast.error(t('errorOccurred')); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex gap-1 flex-wrap mt-2">
      {statuses.map(s => (
        <button key={s} onClick={() => change(s)} disabled={saving}
          className={`text-[10px] px-2 py-1 rounded-full border ${item.status === s ? 'bg-primary/10 border-primary/40 text-primary font-semibold' : 'border-border text-muted-foreground hover:bg-secondary'}`}
          data-testid={`feedback-admin-status-${s}`}>
          {t(`fb_status_${s}`)}
        </button>
      ))}
    </div>
  );
};

const FeedbackCard = ({ item, onChanged, onDeleted }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const meta = CATEGORY_META[item.category] || CATEGORY_META.other;
  const Icon = meta.icon;
  const [upv, setUpv] = useState(false);
  const status = STATUS_STYLES[item.status] || STATUS_STYLES.open;

  const toggle = async () => {
    if (upv) return;
    setUpv(true);
    try {
      const res = await api.post(`/feedback/${item.id}/upvote`);
      onChanged && onChanged({ ...item, upvoted_by_me: res.data.upvoted, upvote_count: res.data.upvote_count });
    } catch { toast.error(t('errorOccurred')); }
    finally { setUpv(false); }
  };

  const [deleting, setDeleting] = useState(false);
  const del = async () => {
    if (deleting || !window.confirm(t('confirmDelete'))) return;
    setDeleting(true);
    try { await api.delete(`/feedback/${item.id}`); onDeleted && onDeleted(item.id); toast.success(t('delete')); }
    catch { toast.error(t('errorOccurred')); }
    finally { setDeleting(false); }
  };

  const canDelete = user?.id === item.author?.id || user?.is_admin;

  return (
    <Card className="p-4 rounded-2xl bg-card border border-border" data-testid="feedback-card">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${meta.color}18`, color: meta.color }}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-semibold uppercase" style={{ color: meta.color }}>{t(`fb_${item.category}`)}</span>
            <span className="text-[10px] rounded-full px-2 py-0.5 font-semibold" style={{ backgroundColor: status.bg, color: status.color }}>{t(`fb_status_${item.status}`)}</span>
          </div>
          <div className="font-display font-semibold text-base leading-tight mt-0.5">{item.title}</div>
          <div className="text-[11px] text-muted-foreground">{item.author?.full_name} · {timeAgo(item.created_at, t)}</div>
          <p className="text-sm mt-2 whitespace-pre-wrap line-clamp-4">{item.description}</p>
          {item.admin_notes && (
            <div className="mt-2 p-2 rounded-lg bg-primary/5 border border-primary/20 text-xs"><b>{t('adminNotes')}:</b> {item.admin_notes}</div>
          )}
          {user?.is_admin && <AdminStatusControl item={item} onUpdated={onChanged} />}
        </div>
        <div className="flex flex-col gap-1">
          {canDelete && (<Button variant="ghost" size="sm" onClick={del} disabled={deleting} aria-label={t('delete')} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>)}
          <Button variant="ghost" size="sm" onClick={toggle} disabled={upv} className={item.upvoted_by_me ? 'text-primary' : ''} data-testid="feedback-upvote-button">
            <ThumbsUp className={`h-4 w-4 mr-1 ${item.upvoted_by_me ? 'fill-primary' : ''}`} /> {item.upvote_count || 0}
          </Button>
        </div>
      </div>
    </Card>
  );
};

const FeedbackPage = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      const res = await api.get('/feedback', { params });
      setItems(res.data || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Lightbulb className="h-6 w-6 text-primary" /> {t('feedbackTitle')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('feedbackSubtitle')}</p>
        </div>
        <CreateDialog onCreated={(x) => setItems(prev => [x, ...prev])} />
      </div>
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-secondary/60 rounded-full p-1 h-auto flex-wrap">
          {['all', 'open', 'in_progress', 'planned', 'done'].map(k => (
            <TabsTrigger key={k} value={k} className="rounded-full text-xs" data-testid={`feedback-tab-${k}`}>
              {k === 'all' ? t('all') : t(`fb_status_${k}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="space-y-2">
        {loading && <div className="text-sm text-muted-foreground">{t('loading')}</div>}
        {!loading && items.length === 0 && (<EmptyState icon={<MessageSquare className="h-6 w-6" />} title={t('noResults')} />)}
        {items.map(i => (
          <motion.div key={i.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
            <FeedbackCard item={i} onChanged={(u) => setItems(prev => prev.map(x => x.id === u.id ? u : x))} onDeleted={(id) => setItems(prev => prev.filter(x => x.id !== id))} />
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default FeedbackPage;
